/**
 * ehr#893: the chart's top tab row (Summary, Profile, Follow-up, Appointments, ...). It shows as many
 * tabs as fit the row and puts the rest, in the same order, under "More" at the end, the way the
 * Profile tab's section row does (ProfileSectionTabs, ehr#743 V15). So the row never scrolls
 * sideways, at any width: the drawer at either size, a tablet or a phone.
 *
 * Same rules as the Profile row's More: while a tab under More is open, More is named for it
 * ("Billing") and underlined as the open tab. A tab's count (Appointments 22) and red dot (Follow-up
 * past due) go with it into the menu; More shows the red dot when a tab inside has one. More is a
 * menu button: Down (or Enter) opens it, Escape, a click outside or focus leaving closes it, and its
 * items are menuitemradio with aria-checked. Arrows move along the row.
 *
 * The fit is measured from a hidden copy of the row and redone when the row changes width
 * (ResizeObserver: window resize, the drawer's Maximize / Restore, a phone turned) or a tab's count
 * changes.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { chartTabsFit, type ChartTab } from '../lib/chartTabs.js';
import { arrowMove, moreLabel } from '../lib/profileTabs.js';
import { Chevron } from './ProfileSectionTabs.js';

const MENU_ID = 'chart-tabs-more-menu';

const tabAria = (t: ChartTab) =>
  [t.label, t.badge ? String(t.badge) : '', t.overdue ? 'past due' : ''].filter(Boolean).join(', ');

function TabInner({ t }: { t: ChartTab }) {
  return (
    <>
      {t.label}
      {t.badge ? <span className="tcount">{t.badge}</span> : null}
    </>
  );
}

export function ChartTabs({
  tabs,
  current,
  onPick,
  initialVisible,
  initialMoreOpen = false,
}: {
  tabs: readonly ChartTab[];
  /** The open tab's key. */
  current: string;
  onPick: (key: string) => void;
  /** For tests (server render, nothing to measure): how many tabs start on the row. */
  initialVisible?: number;
  /** For tests: start with the More list open. */
  initialMoreOpen?: boolean;
}) {
  const [visible, setVisible] = useState(initialVisible ?? tabs.length);
  const [open, setOpen] = useState(initialMoreOpen);
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const shown = Math.min(visible, tabs.length);
  const row = tabs.slice(0, shown);
  const more = tabs.slice(shown);
  const inMore = more.some((t) => t.key === current);
  const moreOverdue = more.some((t) => t.overdue);
  const rovingKey = row.some((t) => t.key === current) || inMore ? current : row[0]?.key;
  const active = tabs.findIndex((t) => t.key === current);
  // What changes a tab's width: its label and count.
  const sig = tabs.map((t) => `${t.key}:${t.label}:${t.badge ?? ''}`).join('|');

  const measure = useCallback(() => {
    const nav = navRef.current;
    const m = measureRef.current;
    if (!nav || !m) return;
    const cs = getComputedStyle(nav);
    const avail =
      nav.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    if (avail <= 0) return; // not laid out (hidden): keep what we have
    const outer = (el: Element) =>
      el.getBoundingClientRect().width + (parseFloat(getComputedStyle(el).marginRight) || 0);
    const tabEls = Array.from(m.querySelectorAll('[data-m="tab"]'));
    const moreEl = m.querySelector('[data-m="more"]');
    const moreActiveEls = Array.from(m.querySelectorAll('[data-m="more-active"]'));
    if (!moreEl || tabEls.length !== tabs.length) return;
    const tabW = tabEls.map(outer);
    const trail =
      parseFloat(getComputedStyle(tabEls[tabEls.length - 1] ?? moreEl).marginRight) || 0;
    const next = chartTabsFit({
      tabW,
      trail,
      avail,
      active,
      moreW: outer(moreEl),
      moreActiveW: moreActiveEls.map(outer),
    });
    setVisible(next);
    // sig: a new count or label changes the widths
  }, [sig, active, tabs.length]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Re-fit when the row changes width (window, Maximize / Restore, phone turned) and once the web
  // font has loaded (Sora is wider than the fallback).
  const measureRefFn = useRef(measure);
  measureRefFn.current = measure;
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    let frame = 0;
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => measureRefFn.current());
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(later) : null;
    ro?.observe(nav);
    window.addEventListener('resize', later);
    void document.fonts?.ready.then(later);
    return () => {
      cancelAnimationFrame(frame);
      ro?.disconnect();
      window.removeEventListener('resize', later);
    };
  }, []);

  // A click anywhere outside, or focus moving outside, closes the More list.
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('focusin', outside);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [open]);
  // Nothing left under More (the row got wider): the list closes.
  useEffect(() => {
    if (!more.length && open) setOpen(false);
  }, [more.length, open]);

  const pick = (key: string) => {
    setOpen(false);
    onPick(key);
  };
  const focusMenuItem = (i: number) => window.setTimeout(() => itemRefs.current[i]?.focus(), 0);
  const rowCount = row.length + (more.length ? 1 : 0);
  // The row's focus order: its tabs, then More.
  const focusRow = (i: number) => {
    if (i < row.length) rowRefs.current[i]?.focus();
    else btnRef.current?.focus();
  };
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const to = arrowMove(e.key, i, rowCount);
    if (to == null) return;
    e.preventDefault();
    focusRow(to);
  };
  const onMoreKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      focusMenuItem(e.key === 'ArrowDown' ? 0 : more.length - 1);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      focusRow(arrowMove(e.key, row.length, rowCount) ?? 0);
    }
  };
  const onItemKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const to = arrowMove(e.key, i, more.length);
    if (to == null) return;
    e.preventDefault();
    itemRefs.current[to]?.focus();
  };

  return (
    <nav className="drawer-tabs" ref={navRef} role="tablist" aria-label="Contact tabs">
      {row.map((t, i) => {
        const on = t.key === current;
        return (
          <button
            key={t.key}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            type="button"
            className={`drawer-tab ${on ? 'active' : ''}${t.overdue ? ' has-overdue' : ''}`}
            role="tab"
            aria-selected={on}
            tabIndex={t.key === rovingKey ? 0 : -1}
            onClick={() => pick(t.key)}
            onKeyDown={(e) => onTabKey(e, i)}
          >
            <TabInner t={t} />
          </button>
        );
      })}
      {more.length > 0 && (
        <div
          className="drawer-more-wrap"
          ref={wrapRef}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && open) {
              e.stopPropagation();
              setOpen(false);
              btnRef.current?.focus();
            }
          }}
          onBlur={(e) => {
            if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
          }}
        >
          <button
            ref={btnRef}
            type="button"
            className={`drawer-tab drawer-more ${inMore ? 'active' : ''}${moreOverdue ? ' has-overdue' : ''}`}
            aria-haspopup="menu"
            aria-controls={MENU_ID}
            aria-expanded={open}
            aria-label={`${inMore ? `${moreLabel(more, current)}, more tabs` : 'More tabs'}${moreOverdue ? ', past due' : ''}`}
            tabIndex={inMore ? 0 : -1}
            onClick={(e) => {
              const next = !open;
              setOpen(next);
              // Opened from the keyboard (Enter / Space): the first item takes focus.
              if (next && e.detail === 0) focusMenuItem(0);
            }}
            onKeyDown={onMoreKey}
          >
            {moreLabel(more, current)}
            <Chevron />
          </button>
          <div
            className={`pst-menu drawer-more-menu${open ? ' open' : ''}`}
            id={MENU_ID}
            role="menu"
          >
            {more.map((t, i) => {
              const on = t.key === current;
              return (
                <button
                  key={t.key}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  aria-label={tabAria(t)}
                  tabIndex={-1}
                  className={`pst-item${on ? ' on' : ''}`}
                  onClick={() => {
                    pick(t.key);
                    btnRef.current?.focus();
                  }}
                  onKeyDown={(e) => onItemKey(e, i)}
                >
                  <span className="pst-l">{t.label}</span>
                  {t.badge ? <span className="tcount">{t.badge}</span> : null}
                  {t.overdue && <span className="drawer-more-odot" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* The hidden copy the fit is measured from: every tab (at the open tab's weight, the widest),
          More, and More named for each tab. Zero-size and clipped, so it never adds a scrollbar. */}
      <div className="drawer-tabs-measure" ref={measureRef} aria-hidden>
        <div className="drawer-tabs-measure-in">
          {tabs.map((t) => (
            <span key={t.key} data-m="tab" className="drawer-tab active">
              <TabInner t={t} />
            </span>
          ))}
          <span data-m="more" className="drawer-tab drawer-more">
            More
            <Chevron />
          </span>
          {tabs.map((t) => (
            <span key={t.key} data-m="more-active" className="drawer-tab drawer-more active">
              {t.label}
              <Chevron />
            </span>
          ))}
        </div>
      </div>
    </nav>
  );
}
